import { ISoccerMatch } from "../../../types";

// Cross-iframe live clock sync. Each frame fetches match data at a different
// moment, so extrapolating from its own dataUpdatedAt drifts the clocks apart
// (CloudFront can serve the same payload with syncedAt up to 30s apart).
// The freshest fetch from any same-tab frame is published to sessionStorage
// and every frame extrapolates from that shared (snapshot, syncedAt) pair.
// sessionStorage is shared across same-origin iframes within one tab.

const KEY_PREFIX = "sbs-clock-";
const SNAPSHOT_MAX_AGE_MS = 10 * 60 * 1000;

interface ClockSnapshot {
  matchDetails: {
    periodId?: number;
    matchLengthMin?: number;
    matchLengthSec?: number;
    matchTime?: number;
  };
  events?: { periodId?: number; timeMin?: number; timestamp?: string }[];
  syncedAt: number;
}

function readSnapshot(matchId: string): ClockSnapshot | null {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + matchId);
    if (!raw) return null;
    const snap = JSON.parse(raw) as ClockSnapshot;
    if (!snap?.syncedAt || Date.now() - snap.syncedAt > SNAPSHOT_MAX_AGE_MS) {
      return null;
    }
    return snap;
  } catch {
    return null;
  }
}

export function publishClockSnapshot(
  matchId: string | undefined,
  match: ISoccerMatch | undefined,
  syncedAt: number | undefined,
): void {
  if (!matchId || !match?.matchDetails || !syncedAt) return;
  try {
    const existing = readSnapshot(matchId);
    if (existing && existing.syncedAt >= syncedAt) return;

    const md = match.matchDetails;
    const periodId = md.periodId;
    const snapshot: ClockSnapshot = {
      matchDetails: {
        periodId: md.periodId,
        matchLengthMin: md.matchLengthMin,
        matchLengthSec: md.matchLengthSec,
        matchTime: md.matchTime,
      },
      // Only current-period events feed the clock fallback - keep it light.
      events: (match.events ?? [])
        .filter(
          (e) =>
            (e as { periodId?: number }).periodId === periodId &&
            e.timeMin != null,
        )
        .slice(-5)
        .map((e) => ({
          periodId: (e as { periodId?: number }).periodId,
          timeMin: e.timeMin,
          timestamp: e.timestamp,
        })),
      syncedAt,
    };
    sessionStorage.setItem(KEY_PREFIX + matchId, JSON.stringify(snapshot));
  } catch {
    // Storage unavailable - frames degrade to their own local clocks.
  }
}

/**
 * Returns whichever clock source is fresher: this frame's local
 * (match, syncedAt) pair or the shared snapshot published by another frame.
 * Consumers call this every heartbeat tick, so a fresher snapshot from a
 * sibling iframe is picked up within a second.
 */
export function freshestClockSource(
  matchId: string | undefined,
  localMatch: ISoccerMatch | undefined,
  localSyncedAt: number | undefined,
): { match: ISoccerMatch | undefined; syncedAt: number | undefined } {
  if (!matchId) return { match: localMatch, syncedAt: localSyncedAt };
  const snap = readSnapshot(matchId);
  if (!snap || (localSyncedAt ?? 0) >= snap.syncedAt) {
    return { match: localMatch, syncedAt: localSyncedAt };
  }
  // Replace the clock fields wholesale - Stats Perform omits zero-value
  // fields and JSON drops undefined keys, so a spread-merge would mix a
  // fresh matchLengthMin with a stale local matchLengthSec.
  return {
    match: {
      ...(localMatch ?? {}),
      matchDetails: {
        ...(localMatch?.matchDetails ?? {}),
        periodId: snap.matchDetails.periodId,
        matchLengthMin: snap.matchDetails.matchLengthMin,
        matchLengthSec: snap.matchDetails.matchLengthSec,
        matchTime: snap.matchDetails.matchTime,
      },
      events: snap.events ?? localMatch?.events,
    } as ISoccerMatch,
    syncedAt: snap.syncedAt,
  };
}
